/**
 * Page layout shared by the canvas and the exporters: paper size, drawing frame with
 * column references, and the title block with the RGIE header fields.
 * Everything is in millimetres, origin at the top-left corner of the paper.
 */
import { latestRevision, sheetsInOrder, type Project, type Sheet, localeTag } from '@elec/domain';
import { translator, type MessageKey } from '@elec/i18n';

export const PAPER_MM = { A4: { w: 297, h: 210 }, A3: { w: 420, h: 297 } } as const;

export interface LayoutLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Pen width in mm. */
  width: number;
}

export interface LayoutText {
  text: string;
  x: number;
  y: number;
  size: number;
  anchor: 'start' | 'middle' | 'end';
  bold?: boolean;
  muted?: boolean;
}

export interface PageLayout {
  width: number;
  height: number;
  /** Drawing area inside the frame (where symbols go). */
  content: { minX: number; minY: number; maxX: number; maxY: number };
  /** Title block rectangle. */
  titleBlock: { x: number; y: number; w: number; h: number };
  lines: LayoutLine[];
  texts: LayoutText[];
}

export function paperSize(sheet: Pick<Sheet, 'paper' | 'orientation'>): { w: number; h: number } {
  const p = PAPER_MM[sheet.paper];
  return sheet.orientation === 'portrait' ? { w: p.h, h: p.w } : { w: p.w, h: p.h };
}

const MARGIN = 10;
const BAND = 5; // column-reference band at the top of the frame
const TB_W = 180;
const TB_ROW = 7;

/** Truncate text so it fits a width (rough glyph width estimate). */
function fit(text: string, size: number, maxW: number): string {
  const max = Math.floor(maxW / (size * 0.56));
  return text.length <= max ? text : `${text.slice(0, Math.max(1, max - 1))}…`;
}

export function pageLayout(project: Project, sheet: Sheet): PageLayout {
  const { w, h } = paperSize(sheet);
  const t = translator(project.meta.locale);
  const lines: LayoutLine[] = [];
  const texts: LayoutText[] = [];
  const line = (x1: number, y1: number, x2: number, y2: number, width = 0.25) =>
    lines.push({ x1, y1, x2, y2, width });
  const box = (x: number, y: number, bw: number, bh: number, width = 0.25) => {
    line(x, y, x + bw, y, width);
    line(x + bw, y, x + bw, y + bh, width);
    line(x + bw, y + bh, x, y + bh, width);
    line(x, y + bh, x, y, width);
  };

  // Frame
  const fx = MARGIN;
  const fy = MARGIN;
  const fw = w - 2 * MARGIN;
  const fh = h - 2 * MARGIN;
  box(fx, fy, fw, fh, 0.5);

  // Column reference band (used by cross-references in control diagrams)
  const cols = sheet.paper === 'A3' ? 12 : 8;
  line(fx, fy + BAND, fx + fw, fy + BAND);
  const cw = fw / cols;
  for (let i = 0; i < cols; i++) {
    if (i > 0) line(fx + i * cw, fy, fx + i * cw, fy + BAND);
    texts.push({
      text: String(i + 1),
      x: fx + (i + 0.5) * cw,
      y: fy + BAND - 1.4,
      size: 2.5,
      anchor: 'middle',
      muted: true,
    });
  }

  // Title block (bottom right): 4 rows × 3 columns
  const tbH = TB_ROW * 4;
  const tbW = Math.min(TB_W, fw);
  const tx = fx + fw - tbW;
  const ty = fy + fh - tbH;
  box(tx, ty, tbW, tbH, 0.5);
  for (let r = 1; r < 4; r++) line(tx, ty + r * TB_ROW, tx + tbW, ty + r * TB_ROW);
  const c1 = tbW * 0.5;
  const c2 = tbW * 0.78;
  line(tx + c1, ty, tx + c1, ty + tbH);
  line(tx + c2, ty, tx + c2, ty + 3 * TB_ROW); // last row: right cells merged

  const sheets = sheetsInOrder(project);
  const index = sheets.findIndex((s) => s.id === sheet.id) + 1;
  const m = project.meta;
  const rev = project.revisions ? latestRevision(project) : undefined;
  const owner = [m.ownerName, m.ownerAddress].filter(Boolean).join(', ');
  const author = [m.author, m.company].filter(Boolean).join(' — ');
  const date = m.date ? new Date(`${m.date}T12:00:00`).toLocaleDateString(localeTag(m.locale)) : '';
  const cells: Array<[number, number, number, string, string, boolean?]> = [
    // row, x offset, width, caption, value, bold
    [0, 0, c1, t('tb.project'), m.title, true],
    [0, c1, c2 - c1, t('tb.sheet'), sheet.name, true],
    [0, c2, tbW - c2, t('tb.sheetNo'), `${index} / ${sheets.length}`, true],
    [1, 0, c1, t('tb.owner'), owner],
    [1, c1, c2 - c1, t('tb.date'), date],
    rev
      ? [1, c2, tbW - c2, t('tb.paperRev'), `${sheet.paper} · ${rev.index}`, true]
      : [1, c2, tbW - c2, t('tb.paper'), `${sheet.paper}`],
    [2, 0, c1, t('tb.installation'), m.installationAddress || owner],
    [2, c1, c2 - c1, t('tb.supply'), m.supplyVoltage],
    [2, c2, tbW - c2, t('tb.earthing'), m.earthingSystem],
    [3, 0, c1, t('tb.author'), author],
    [3, c1, tbW - c1, t('tb.diagram'), t(`sheetKind.${sheet.kind}` as MessageKey)],
  ];
  for (const [row, ox, cwid, caption, value, bold] of cells) {
    const x = tx + ox + 1.2;
    const y = ty + row * TB_ROW;
    texts.push({ text: caption, x, y: y + 2.3, size: 1.8, anchor: 'start', muted: true });
    if (value)
      texts.push({
        text: fit(value, 2.8, cwid - 2.4),
        x,
        y: y + 5.9,
        size: 2.8,
        anchor: 'start',
        bold: !!bold,
      });
  }

  return {
    width: w,
    height: h,
    content: { minX: fx, minY: fy + BAND, maxX: fx + fw, maxY: ty },
    titleBlock: { x: tx, y: ty, w: tbW, h: tbH },
    lines,
    texts,
  };
}

/** Column reference (1-based) of an x position on a sheet, for cross-references. */
export function columnAt(sheet: Sheet, x: number): number {
  const { w } = paperSize(sheet);
  const cols = sheet.paper === 'A3' ? 12 : 8;
  const fw = w - 2 * MARGIN;
  return Math.min(cols, Math.max(1, Math.floor((x - MARGIN) / (fw / cols)) + 1));
}
